import type { ProjectState, Run } from "@minga/core"
import { isOpenProposal, verbOfProposal } from "@minga/core"
import {
  type NeedsVerb,
  needsVerbs,
  type PlanLayout,
  type ProjectTab,
  planLayouts,
  projectTabs,
} from "./project-views"

export type PlanStatusFilter =
  | "all"
  | "triage"
  | "backlog"
  | "todo"
  | "in_progress"
  | "in_review"
  | "blocked"
  | "done"
  | "cancelled"
export type PlanSort = "due" | "updated"
export type PlanGroup = "area" | "owner" | "status" | "type" | "none"
export type OutputsFilter = "waiting" | "picked" | "rejected"
/** Plan Board lanes (decision 0112): `area` (default, omitted) · `milestone` · `none`. */
export type BoardLanes = "milestone" | "none"

/**
 * The project page's search params (decision 0056 §3.1). Every param of every tab is defined here, so the tab views
 * never edit this file. Defaults are omitted from the URL. Pure, so the rules are unit-tested.
 */
export interface ProjectSearch {
  view?: ProjectTab
  /** Plan only: List (default, omitted) · Board · Canvas · Outputs. */
  layout?: Exclude<PlanLayout, "list">
  /** Canvas scope: an item ref or id, or `project` (the whole project). Absent: the current milestone. */
  focus?: string
  /** The selected item (a ref or an id): the Canvas inspector, the List row highlight. */
  node?: string
  /** Plan List/Outputs: `open` (default, omitted) · `all` · an item status (`done` included). */
  status?: PlanStatusFilter
  /** A type key, or `milestone` (release, bet and workstream). */
  type?: string
  /** `me` · `user:<id>` · `agent:<id>`. */
  owner?: string
  assignee?: "person" | "worker"
  /** An item id or ref, or `none` (Not in a milestone). */
  milestone?: string
  area?: string
  goal?: string
  label?: string
  /** Plan List: `rank` (default, omitted) · `due` · `updated`. */
  sort?: PlanSort
  /** Plan List: `milestone` (default, omitted) · `area` · `owner` · `status` · `type` · `none`. */
  group?: PlanGroup
  /** Plan Outputs: `all` (default, omitted) · `waiting` · `picked` · `rejected`. */
  outputs?: OutputsFilter
  /** Plan Board (decision 0112): the swimlanes, `area` (default, omitted) · `milestone` · `none`. */
  lanes?: BoardLanes
  /** Plan Board: `all` shows every done item; absent, Done holds the last 7 days. */
  done?: "all"
  /** Needs you: the group to scroll to. */
  verb?: NeedsVerb
  /** Needs you: the card (a proposal id) to scroll to and highlight; wins over `verb`. */
  card?: string
  /** A run whose transcript is open (`?run=<runId>`, from the runners page, rows and notifications). */
  run?: string
  /** The Runs sheet is open (`?runs=1`). */
  runs?: 1
  // ---- wave K (decision 0065, spec §10.4) ----
  /**
   * Chat only: whose chat to open: the agent's id (wave K search hits, `chatId` = the agent's id today) or its handle
   * (wave E, decision 0063, `?agent=<handle>`); absent = the lead's. `chatAgentOf` accepts either.
   */
  agent?: string
  /** Chat only: the message to scroll to (a past-discussions hit). */
  msg?: string
  /** Chat only: a question typed into the composer, not sent ("Ask Lea" on the search page). */
  ask?: string
  /** With `run`: the transcript frame to scroll to (a past-runs hit). */
  frame?: number
  /** An output open in the viewer over any page (`O-n` chips, search hits): its artifact id. */
  output?: string
  /** A decided card open in a sheet over any page (`P-n` chips of cards no longer in the state): its proposal id. */
  decision?: string
}

/** The tab a project opens on without `?view=` (decision 0051): the Overview, on desktop and phone. */
export const defaultProjectView = (view: ProjectTab | undefined): ProjectTab => view ?? "overview"

const RUN_ID = /^run_[0-9a-z]{1,64}$/
const KEY = /^[A-Za-z0-9_-]{1,64}$/
const ACTOR = /^(me|(user|agent):[A-Za-z0-9_-]{1,64})$/
const LABEL = /^[^\s]{1,64}$/
const MESSAGE_ID = /^[A-Za-z0-9_:.-]{1,128}$/
/** "Ask Lea": at most this many characters of the question are carried in the URL. */
export const ASK_MAX = 500
const STATUSES: ReadonlyArray<PlanStatusFilter> = [
  "all",
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "cancelled",
]
const SORTS: ReadonlyArray<PlanSort> = ["due", "updated"]
const GROUPS: ReadonlyArray<PlanGroup> = ["area", "owner", "status", "type", "none"]
const OUTPUTS: ReadonlyArray<OutputsFilter> = ["waiting", "picked", "rejected"]
const LANES: ReadonlyArray<BoardLanes> = ["milestone", "none"]

const str = (v: unknown, re: RegExp): string | undefined =>
  typeof v === "string" && re.test(v) ? v : undefined
const oneOf = <T extends string>(list: ReadonlyArray<T>, v: unknown): T | undefined =>
  list.find((x) => x === v)

/** Every param checked; anything else is dropped. Plan params only with `view=plan`, Needs-you params with `needs`. */
export const validateProjectSearch = (search: Record<string, unknown>): ProjectSearch => {
  const out: ProjectSearch = {}
  const view = oneOf(projectTabs, search.view)
  if (view) out.view = view
  if (view === "plan") {
    const layout = oneOf(planLayouts, search.layout)
    if (layout && layout !== "list") out.layout = layout
    const focus = str(search.focus, KEY)
    if (focus) out.focus = focus
    const status = oneOf(STATUSES, search.status)
    if (status) out.status = status
    const type = str(search.type, KEY)
    if (type) out.type = type
    const owner = str(search.owner, ACTOR)
    if (owner) out.owner = owner
    const assignee = oneOf(["person", "worker"] as const, search.assignee)
    if (assignee) out.assignee = assignee
    const milestone = str(search.milestone, KEY)
    if (milestone) out.milestone = milestone
    const area = str(search.area, KEY)
    if (area) out.area = area
    const goal = str(search.goal, KEY)
    if (goal) out.goal = goal
    const label = str(search.label, LABEL)
    if (label) out.label = label
    const sort = oneOf(SORTS, search.sort)
    if (sort) out.sort = sort
    const group = oneOf(GROUPS, search.group)
    if (group) out.group = group
    const outputs = oneOf(OUTPUTS, search.outputs)
    if (outputs) out.outputs = outputs
    const lanes = oneOf(LANES, search.lanes)
    if (lanes) out.lanes = lanes
    if (search.done === "all") out.done = "all"
  }
  if (view === "needs") {
    const verb = oneOf(needsVerbs, search.verb)
    if (verb) out.verb = verb
    const card = str(search.card, KEY)
    if (card) out.card = card
  }
  if (typeof search.node === "string" && search.node && search.node.length <= 64) out.node = search.node
  if (view === "chat") {
    const agent = str(search.agent, KEY)
    if (agent) out.agent = agent
    const msg = str(search.msg, MESSAGE_ID)
    if (msg) out.msg = msg
    if (typeof search.ask === "string" && search.ask.trim()) out.ask = search.ask.slice(0, ASK_MAX)
  }
  const run = str(search.run, RUN_ID)
  if (run) out.run = run
  if (search.runs === 1 || search.runs === "1" || search.runs === true) out.runs = 1
  const frame = typeof search.frame === "string" ? Number(search.frame) : search.frame
  if (run && typeof frame === "number" && Number.isSafeInteger(frame) && frame > 0) out.frame = frame
  const output = str(search.output, KEY)
  if (output) out.output = output
  const decision = str(search.decision, KEY)
  if (decision) out.decision = decision
  return out
}

/**
 * Old view URLs (before decision 0056) → the new search, or null when the URL is not an old one. Reads the raw search
 * string (`?view=graph` no longer validates). `run`, `runs`, `focus` and `node` survive. An unknown view
 * (`?view=bogus`) is dropped: the Overview, with `run`/`runs` kept.
 */
export const legacyProjectSearch = (raw: URLSearchParams): ProjectSearch | null => {
  const view = raw.get("view")
  const keep: ProjectSearch = {}
  const run = str(raw.get("run"), RUN_ID)
  if (run) keep.run = run
  const runs = raw.get("runs")
  if (runs === "1" || runs === "true") keep.runs = 1
  const node = raw.get("node")
  const withNode = node && node.length <= 64 ? { node } : {}
  switch (view) {
    case "areas":
      return { view: "team", ...keep }
    case "graph": {
      const focus = str(raw.get("focus"), KEY)
      return { view: "plan", layout: "canvas", ...(focus ? { focus } : {}), ...withNode, ...keep }
    }
    case "list":
      return { view: "plan", ...withNode, ...keep }
    case "roadmap":
      return { view: "plan", ...keep }
    case "mine":
      return { view: "needs", verb: "do", ...keep }
    case "triage":
      return { view: "needs", verb: "sort", ...keep }
    case "inbox":
      return { view: "needs", ...keep }
    default:
      // An unknown view is dropped (the Overview), so a copied link is right; a tab or no view needs nothing.
      return view !== null && !(projectTabs as ReadonlyArray<string>).includes(view) ? keep : null
  }
}

/**
 * Opening a run: its transcript (`run=<runId>`), or the agent-chat shortcut with sheets closed. Native details
 * callers select run_do after checking canInspectRunDetails.
 */
export const openRunSearch = (
  prev: ProjectSearch,
  runId: string,
  sink: "run_do" | "agent" | "none" | null,
): ProjectSearch => {
  if (sink !== null && sink !== "run_do") return { view: "chat" }
  return { ...prev, run: runId }
}

/** Native turns retain their Run DO frames as well as streaming into their agent's chat. */
export const canInspectRunDetails = (run: Pick<Run, "sink" | "executor"> | undefined): boolean =>
  run !== undefined && (run.sink?._tag === "run_do" || run.executor?._tag === "runner")

/** Switching tabs keeps an open transcript or Runs sheet; every other param belongs to the old tab. */
export const viewSearch = (prev: ProjectSearch, view: ProjectTab, extra?: ProjectSearch): ProjectSearch => ({
  ...(prev.run ? { run: prev.run, ...(prev.frame ? { frame: prev.frame } : {}) } : {}),
  ...(prev.runs ? { runs: prev.runs } : {}),
  view,
  ...extra,
})

const PLAN_KEYS = [
  "layout",
  "focus",
  "node",
  "status",
  "type",
  "owner",
  "assignee",
  "milestone",
  "area",
  "goal",
  "label",
  "sort",
  "group",
  "outputs",
  "lanes",
  "done",
] as const satisfies ReadonlyArray<keyof ProjectSearch>

/**
 * A Plan URL: keeps `view=plan`, the plan params and an open sheet, then applies `patch` (a key set to `undefined`
 * removes it). Defaults (`layout=list`) are dropped.
 */
export type SearchPatch = { readonly [K in keyof ProjectSearch]?: ProjectSearch[K] | undefined }

export const planSearch = (prev: ProjectSearch, patch: SearchPatch): ProjectSearch => {
  const out: ProjectSearch = { view: "plan" }
  if (prev.view === "plan")
    for (const k of PLAN_KEYS) if (prev[k] !== undefined) Object.assign(out, { [k]: prev[k] })
  if (prev.run) out.run = prev.run
  if (prev.runs) out.runs = prev.runs
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete out[k as keyof ProjectSearch]
    else Object.assign(out, { [k]: v })
  }
  if ((out.layout as string | undefined) === "list") delete out.layout
  out.view = "plan"
  return out
}

/** A Needs-you URL: the group to scroll to, or a card to highlight. */
export const needsSearch = (target: { verb?: NeedsVerb; card?: string } = {}): ProjectSearch => ({
  view: "needs",
  ...(target.verb ? { verb: target.verb } : {}),
  ...(target.card ? { card: target.card } : {}),
})

/**
 * Where "open card" goes (decision 0056 §3.3): a proposal → its card in Needs you, in its verb's group (closed or
 * unknown → the tab); a run → its open permission prompt (Allow), else the Allow group; nothing → the tab.
 */
export const cardSearch = (
  s: ProjectState,
  target: { readonly runId?: string | undefined; readonly proposalId?: string | undefined },
): ProjectSearch => {
  if (target.proposalId) {
    const p = s.proposals.find((x) => x.id === target.proposalId)
    if (p?.state !== "open" || !isOpenProposal(p)) return { view: "needs" }
    return { view: "needs", verb: verbOfProposal(s, p), card: p.id }
  }
  if (target.runId) {
    const p = s.proposals.find(
      (x) =>
        x.state === "open" &&
        isOpenProposal(x) &&
        x.body._tag === "permission" &&
        x.body.runId === target.runId,
    )
    return p ? { view: "needs", verb: "allow", card: p.id } : { view: "needs", verb: "allow" }
  }
  return { view: "needs" }
}

export const without = <K extends keyof ProjectSearch>(prev: ProjectSearch, ...keys: K[]): ProjectSearch => {
  const out = { ...prev }
  for (const k of keys) delete out[k]
  return out
}
